/**
 * clientOcr.ts — client-side text extraction for KYC documents.
 *
 * Three paths:
 *   1. PDF with embedded text layer  → extract instantly via pdf.js
 *   2. Scanned PDF (no text layer)   → rasterize each page → Tesseract OCR
 *   3. Image (JPG/PNG/WEBP/GIF)      → Tesseract OCR directly
 *
 * All heavy libraries (pdfjs-dist, tesseract.js) are lazy-loaded so they
 * don't appear in the main bundle.
 *
 * Privacy guarantee: no bytes ever leave the browser for OCR purposes.
 * The original file upload to /docs/upload is a separate operation.
 */

export type OcrResult = {
  text: string;
  method: 'pdf-text-layer' | 'pdf-rasterize-tesseract' | 'image-tesseract' | 'unsupported';
  pageCount?: number;
  confidence?: number;
};

export async function extractText(
  file: File,
  onProgress?: (pct: number, label: string) => void,
): Promise<OcrResult> {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  const mime = file.type ?? '';

  // Explicit HEIC/HEIF check — browser canvas can't decode these natively
  if (['heic', 'heif'].includes(ext) || mime === 'image/heic' || mime === 'image/heif') {
    console.warn('[KYC OCR] HEIC/HEIF detected — not supported by browser OCR. User must re-export as JPG/PNG.');
    return { text: '', method: 'unsupported' };
  }

  // Extension-first matching; fall back to MIME type for files without extensions
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'tiff', 'bmp'].includes(ext)
    || (ext === '' && /^image\//.test(mime) && !['image/heic', 'image/heif'].includes(mime));
  const isPdf = ext === 'pdf' || mime === 'application/pdf';

  // ─── PDF path ───────────────────────────────────────────────────────────────
  if (isPdf) {
    onProgress?.(5, 'Reading PDF…');
    console.log('[KYC OCR] loading pdfjs-dist chunk');
    const pdfjs = await import('pdfjs-dist');

    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();

    const arrayBuf = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuf }).promise;
    console.log('[KYC OCR] PDF loaded, pages:', pdf.numPages);

    // 1a. Try text layer first (instant, no Tesseract)
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = (content.items as any[]).map((it) => it.str ?? '').join(' ');
      fullText += pageText + '\n\n';
    }

    const nonWs = fullText.replace(/\s/g, '').length;
    console.log('[KYC OCR] text layer chars (non-whitespace):', nonWs);
    if (nonWs >= 200) {
      onProgress?.(100, 'Text layer extracted');
      console.log('[KYC OCR] extraction complete: method=pdf-text-layer, textLength:', fullText.length);
      return { text: fullText.slice(0, 150_000), method: 'pdf-text-layer', pageCount: pdf.numPages };
    }

    // 1b. Scanned PDF — rasterize + Tesseract
    const n = pdf.numPages;
    onProgress?.(15, `Scanned PDF — OCR'ing ${n} page${n > 1 ? 's' : ''}…`);
    console.log('[KYC OCR] text layer empty — loading tesseract.js chunk for scanned PDF');
    const Tesseract = await import('tesseract.js');
    console.log('[KYC OCR] tesseract chunk loaded');
    const worker = await Tesseract.createWorker('eng');
    let ocrText = '';
    for (let i = 1; i <= n; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      console.log(`[KYC OCR] page ${i}/${n} rasterized (${viewport.width}x${viewport.height}), OCR'ing…`);
      await (page.render as any)({ canvas, viewport }).promise;
      const blob: Blob = await new Promise((res) => canvas.toBlob((b) => res(b!), 'image/png'));
      const { data } = await worker.recognize(blob);
      ocrText += data.text + '\n\n';
      onProgress?.(15 + Math.round((i / n) * 80), `OCR page ${i}/${n}…`);
    }
    await worker.terminate();
    onProgress?.(100, 'OCR complete');
    console.log('[KYC OCR] extraction complete: method=pdf-rasterize-tesseract, textLength:', ocrText.length);
    return { text: ocrText.slice(0, 150_000), method: 'pdf-rasterize-tesseract', pageCount: n };
  }

  // ─── Image path ───────────────────────────────────────────────────────────────
  if (isImage) {
    onProgress?.(10, 'Recognising image…');
    console.log('[KYC OCR] loading tesseract.js chunk for image');
    const Tesseract = await import('tesseract.js');
    console.log('[KYC OCR] tesseract chunk loaded');
    const worker = await Tesseract.createWorker('eng');
    const { data } = await worker.recognize(file);
    await worker.terminate();
    onProgress?.(100, 'OCR complete');
    console.log('[KYC OCR] extraction complete: method=image-tesseract, textLength:', data.text.length, 'confidence:', (data.confidence ?? 0).toFixed(1));
    return { text: data.text.slice(0, 150_000), method: 'image-tesseract', confidence: data.confidence };
  }

  // Unsupported format (DOCX, TXT, etc.) — server will handle text extraction
  console.log('[KYC OCR] unsupported format, skipping browser OCR:', ext || mime || '(no ext/mime)');
  return { text: '', method: 'unsupported' };
}

